import { z } from 'zod';
import { REDIS_SCHEMA } from '@ab-inbev-eu/utils/config';

const IS_DEV_MODE = process.env.NODE_ENV === 'development';
const logLevelSchema = z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).optional().default('info');

function requiredInProd(type: z.ZodType) {
  return type.refine((v) => IS_DEV_MODE || !!v);
}

// z.coerce.boolean() treats any non-empty string as true, so "false" in env became true
function envBoolean(defaultValue: boolean) {
  return z.preprocess((v) => {
    if (typeof v !== 'string' || v.trim() === '') return undefined;
    return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase());
  }, z.boolean().optional().default(defaultValue));
}

const publicConfigSchema = z.object({
  NEXT_PUBLIC_BASE_PATH: z.string().optional(),
  NEXT_PUBLIC_LOG_LEVEL: logLevelSchema,
  IS_DEV_MODE: z.boolean(),
  LOCIZE: z.object({
    PROJECT_ID: requiredInProd(z.uuid().optional()) as unknown as z.ZodString,
    API_KEY: requiredInProd(z.uuid().optional()) as unknown as z.ZodString,
    CACHE_TTL_SEC: z.coerce
      .number()
      .optional()
      .default(5 * 60),
    DEBUG: envBoolean(false),
    VERSION: z.string().optional(),
    SAVE_MISSING: envBoolean(false),
  }),
  IS_SERVER_SIDE: z.boolean(),
  SEGMENT: z.object({
    WRITE_KEY: z.string().optional(),
  }),
  SMS_WAIT_TIME_SEC: z.coerce.number().optional().default(30),
  RECAPTURE_SITE_KEY: requiredInProd(z.string().optional()) as unknown as z.ZodString,
  DATADOG: z.object({
    APPLICATION_ID: z.uuid().optional(),
    CLIENT_TOKEN: z.string().optional(),
    SERVICE: z.string().optional(),
    SESSION_SAMPLE_RATE: z.coerce.number().default(100), // 100%
    SESSION_REPLAY_SAMPLE_RATE: z.coerce.number().default(20), // 20%
    TRACK_USER_INTERACTION: envBoolean(true),
    TRACK_RESOURCES: envBoolean(true),
    TRACK_LONG_TASKS: envBoolean(true),
    DEFAULT_PRIVACY_LEVEL: z.enum(['allow', 'mask', 'mask-user-input']).default('mask-user-input'),
    ENABLE_RUM_TRACING: envBoolean(false),
  }),
  ENVIRONMENT: z.string().default('development'),
  BUILD_VERSION: z.string().optional(),
  GOOGLE_MAP: z
    .object({
      API_KEY: z.string(),
      MAP_ID: z.string(),
    })
    .optional(),
  BRAZE: z.object({
    API_KEY: z.string().optional(),
    SDK_ENDPOINT: z.string().optional(),
  }),
  CASHBACK_REQUESTS_COUNTER_REFRESH_INTERVAL_SEC: z.coerce.number().optional().default(10),
});

const privateConfigSchema = z.object({
  DTC_USER_SRV_URL: z.url(),
  DTC_CAMPAIGN_SRV_URL: z.url(),
  ENGAGEMENT_SRV_URL: z.url(),
  ENGAGEMENT_SRV_STATIC_API_KEY: z.uuid(),
  LOG_LEVEL: logLevelSchema,
  SESSION_STORAGE_ENCRYPTION_KEY: z.string().length(32),
  REVALIDATION_TOKEN: z.uuid(),
  FEATURES: z.object({
    TEST_PHONE_NUMBERS_PATTERN: z.string().optional(),
    RECAPTCHA_TEST_PHONE_NUMBERS_PATTERN: z.string().optional(),
    FALLBACK_BYPASS_KEY: z.string().optional(),
  }),
  RECAPTURE: z.object({
    SCORE_THRESHOLD: requiredInProd(z.coerce.number().optional().default(0.5)) as unknown as z.ZodNumber,
    GOOGLE_CLOUD_PROJECT_ID: requiredInProd(z.string().optional()) as unknown as z.ZodString,
    GCP_CLIENT_EMAIL: z.string().optional(),
    GCP_PRIVATE_KEY: z.string().optional(),
  }),
  REDIS: REDIS_SCHEMA.extend({
    CAMPAIGN_KEY_PREFIX: z.string().optional().default('campaign-db'),
    ACTION_KEY_PREFIX: z.string().optional().default('action-db'),
  }),
  CAMPAIGN_SRV_STATIC_API_KEY: z.string(),
  ENCRYPTION_KEY: z.string().min(32).max(64),
  BRAZE: z.object({
    API_KEY: z.string().optional(),
    ENDPOINT: z.string().optional(),
  }),
});

const publicEnv: Record<keyof z.infer<typeof publicConfigSchema>, string | undefined | boolean | object> = {
  NEXT_PUBLIC_BASE_PATH: process.env['NEXT_PUBLIC_BASE_PATH'],
  NEXT_PUBLIC_LOG_LEVEL: process.env['LOG_LEVEL'], // Inherit from LOG_LEVEL, required by Next.js
  IS_DEV_MODE,
  LOCIZE: {
    PROJECT_ID: process.env['NEXT_PUBLIC_LOCIZE_PROJECT_ID'],
    API_KEY: process.env['NEXT_PUBLIC_LOCIZE_API_KEY'],
    CACHE_TTL_SEC: process.env['NEXT_PUBLIC_LOCIZE_CACHE_TTL_SEC'],
    DEBUG: process.env['NEXT_PUBLIC_LOCIZE_DEBUG'],
    VERSION: process.env['NEXT_PUBLIC_LOCIZE_VERSION'],
    SAVE_MISSING: process.env['NEXT_PUBLIC_LOCIZE_SAVE_MISSING'],
  },
  IS_SERVER_SIDE: typeof window === 'undefined',
  SEGMENT: {
    WRITE_KEY: process.env['NEXT_PUBLIC_SEGMENT_WRITE_KEY'],
  },
  SMS_WAIT_TIME_SEC: process.env['NEXT_PUBLIC_SMS_WAIT_TIME_SEC'],
  RECAPTURE_SITE_KEY: process.env['NEXT_PUBLIC_RECAPTURE_SITE_KEY'],
  DATADOG: {
    APPLICATION_ID: process.env['NEXT_PUBLIC_DATADOG_APPLICATION_ID'],
    CLIENT_TOKEN: process.env['NEXT_PUBLIC_DATADOG_CLIENT_TOKEN'],
    SERVICE: process.env['NEXT_PUBLIC_DATADOG_SERVICE'],
    SESSION_SAMPLE_RATE: process.env['NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE'],
    SESSION_REPLAY_SAMPLE_RATE: process.env['NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE'],
    TRACK_USER_INTERACTION: process.env['NEXT_PUBLIC_DATADOG_TRACK_USER_INTERACTION'],
    TRACK_RESOURCES: process.env['NEXT_PUBLIC_DATADOG_TRACK_RESOURCES'],
    TRACK_LONG_TASKS: process.env['NEXT_PUBLIC_DATADOG_TRACK_LONG_TASKS'],
    DEFAULT_PRIVACY_LEVEL: process.env['NEXT_PUBLIC_DATADOG_DEFAULT_PRIVACY_LEVEL'],
    ENABLE_RUM_TRACING: process.env['NEXT_PUBLIC_DATADOG_ENABLE_RUM_TRACING'],
  },
  ENVIRONMENT: process.env['NEXT_PUBLIC_ENVIRONMENT'],
  BUILD_VERSION: process.env['NEXT_PUBLIC_BUILD_VERSION'],
  GOOGLE_MAP: {
    API_KEY: process.env['NEXT_PUBLIC_GOOGLE_MAP_API_KEY'],
    MAP_ID: process.env['NEXT_PUBLIC_GOOGLE_MAP_ID'],
  },
  BRAZE: {
    API_KEY: process.env['NEXT_PUBLIC_BRAZE_API_KEY'],
    SDK_ENDPOINT: process.env['NEXT_PUBLIC_BRAZE_SDK_ENDPOINT'],
  },
  CASHBACK_REQUESTS_COUNTER_REFRESH_INTERVAL_SEC:
    process.env['NEXT_PUBLIC_CASHBACK_REQUESTS_COUNTER_REFRESH_INTERVAL_SEC'],
};

const privateEnv: Record<keyof z.infer<typeof privateConfigSchema>, string | undefined | object> = {
  DTC_USER_SRV_URL: process.env['DTC_USER_SRV_URL'],
  DTC_CAMPAIGN_SRV_URL: process.env['DTC_CAMPAIGN_SRV_URL'],
  ENGAGEMENT_SRV_URL: process.env['ENGAGEMENT_SRV_URL'],
  ENGAGEMENT_SRV_STATIC_API_KEY: process.env['ENGAGEMENT_SRV_STATIC_API_KEY'],
  LOG_LEVEL: process.env['LOG_LEVEL'],
  SESSION_STORAGE_ENCRYPTION_KEY: process.env['SESSION_STORAGE_ENCRYPTION_KEY'],
  REVALIDATION_TOKEN: process.env['REVALIDATION_TOKEN'],
  FEATURES: {
    TEST_PHONE_NUMBERS_PATTERN: process.env['FEATURES_TEST_PHONE_NUMBERS_PATTERN'],
    RECAPTCHA_TEST_PHONE_NUMBERS_PATTERN: process.env['FEATURES_RECAPTCHA_TEST_PHONE_NUMBERS_PATTERN'],
    FALLBACK_BYPASS_KEY: process.env['FEATURES_FALLBACK_BYPASS_KEY'],
  },
  RECAPTURE: {
    GOOGLE_CLOUD_PROJECT_ID: process.env['GOOGLE_CLOUD_PROJECT_ID'],
    SCORE_THRESHOLD: process.env['RECAPTURE_SCORE_THRESHOLD'],
    GCP_CLIENT_EMAIL: process.env['RECAPTURE_GCP_CLIENT_EMAIL'],
    GCP_PRIVATE_KEY:
      process.env['RECAPTURE_GCP_PRIVATE_KEY_BASE64'] &&
      Buffer.from(process.env['RECAPTURE_GCP_PRIVATE_KEY_BASE64'], 'base64')
        .toString('utf8')
        .split(String.raw`\n`)
        .join('\n'),
  },
  REDIS: {
    HOST: process.env['REDIS_HOST'],
    PORT: process.env['REDIS_PORT'],
    PASSWORD: process.env['REDIS_PASSWORD'],
    CAMPAIGN_KEY_PREFIX: process.env['REDIS_CAMPAIGN_KEY_PREFIX'],
    ACTION_KEY_PREFIX: process.env['REDIS_ACTION_KEY_PREFIX'],
  },
  CAMPAIGN_SRV_STATIC_API_KEY: process.env['CAMPAIGN_SRV_STATIC_API_KEY'],
  ENCRYPTION_KEY: process.env['ENCRYPTION_KEY'],
  BRAZE: {
    API_KEY: process.env['BRAZE_API_KEY'],
    ENDPOINT: process.env['BRAZE_API_ENDPOINT'],
  },
};

let parsedPublicEnv: z.infer<typeof publicConfigSchema> | undefined;
let parsedPrivateEnv: z.infer<typeof privateConfigSchema> | undefined;

function publicConfig() {
  if (!parsedPublicEnv) parsedPublicEnv = publicConfigSchema.parse(publicEnv);
  return parsedPublicEnv;
}

function privateConfig() {
  if (!parsedPrivateEnv) parsedPrivateEnv = privateConfigSchema.parse(privateEnv);
  return parsedPrivateEnv;
}

export { publicConfig, privateConfig };
